"use client";

import { motion } from "motion/react";
import { History, X } from "lucide-react";

interface PrefilledAnswersBannerProps {
  /** Clears the filled-in answers and hides the banner. */
  onStartBlank: () => void;
  /** Hides the banner and keeps the answers. */
  onDismiss: () => void;
}

/**
 * Tells a signed-in user that a fresh wizard was filled in from their last event, with a way to
 * clear those answers. Styled to match {@link ResumeDraftBanner}, which takes this slot when a draft
 * is resumed instead. Plain buttons rather than `FetchButton`: this renders on the server, where
 * `FetchButton` can't resolve the enterprise brand color.
 *
 * @param props - See {@link PrefilledAnswersBannerProps}.
 * @returns The banner.
 */
export const PrefilledAnswersBanner = ({ onStartBlank, onDismiss }: PrefilledAnswersBannerProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="flex items-start gap-3 rounded-2xl bg-indigo-50/70 px-4 py-3 text-left ring-1 ring-inset ring-indigo-100"
      role="status"
    >
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-indigo-600">
        <History className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
      </span>
      <div className="flex flex-1 items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-semibold text-gray-900 tracking-tight">
            We filled in some answers from your last event
          </p>
          <p className="text-sm font-medium text-gray-700">
            Headcount, rooms, catering and budget. Change any of them.
          </p>
        </div>
        <button
          type="button"
          onClick={onStartBlank}
          className="shrink-0 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
        >
          Start blank
        </button>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="mt-0.5 shrink-0 text-gray-400 hover:text-gray-600"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </motion.div>
  );
};
